import * as chatwoot from "ahize/chatwoot";

/**
 * Lazy Chatwoot Chat composable (ahize/chatwoot)
 *
 * Loads Chatwoot on-demand via the ahize unified SDK.
 * Hides the bubble using a body CSS class (not SDK hide) to avoid
 * breaking the messaging backend initialization.
 */

// Module-level singletons — persist across component mount/unmount cycles
let chatwootInitialized = false;
const globalIsChatOpen = ref(false);

/** Toggle body class that controls CSS visibility */
function setBodyChatVisible(visible: boolean) {
  if (!import.meta.client) return;
  if (visible) {
    document.body.classList.add("chat-widget-opened");
  } else {
    document.body.classList.remove("chat-widget-opened");
  }
}

export function useLazyCrisp() {
  const auth = useAuthStore();
  const route = useRoute();
  const toast = useToast();

  const isLoading = ref(false);
  const isChatOpen = globalIsChatOpen;

  function isGenogram(): boolean {
    return route.path.includes("/tools/genogram");
  }

  /** Sync Vue state with Chatwoot widget events */
  function registerListeners() {
    chatwoot.on?.("opened", () => {
      globalIsChatOpen.value = true;
      setBodyChatVisible(true);
    });

    chatwoot.on?.("closed", () => {
      globalIsChatOpen.value = false;
      setBodyChatVisible(false);
    });
  }

  /** Initialize Chatwoot, identify user, and open chat */
  async function initAndOpen() {
    await chatwoot.load({
      websiteToken: "8E1Ubxe32ScarYYKAV48F8FW",
      hideMessageBubble: true,
      type: "standard",
      position: "right",
    });
    chatwootInitialized = true;

    // Identify user
    const user = unref(auth.user);
    if (user) {
      const fullName = `${user.firstname || ""} ${user.surname || ""}`.trim();
      await chatwoot.identify({
        id: String(user.id || user.email),
        email: user.email,
        name: fullName || undefined,
      });
    }

    registerListeners();

    // Show and open the chat widget
    await chatwoot.show();
    globalIsChatOpen.value = true;
    setBodyChatVisible(true);
  }

  /** Toggle chat open/closed. If not yet loaded, lazy-loads first. */
  async function toggleChat() {
    if (isGenogram()) {
      return;
    }

    // Not initialized yet — load on first click
    if (!chatwootInitialized) {
      isLoading.value = true;
      try {
        await initAndOpen();
      } catch (error) {
        console.error("Failed to load Chatwoot chat:", error);
        toast.add({
          title: "Support chat unavailable",
          description: "Please email support@familyaxis.com for assistance.",
          color: "error",
        });
      } finally {
        isLoading.value = false;
      }
      return;
    }

    // Already loaded — toggle
    if (isChatOpen.value) {
      await chatwoot.hide();
      globalIsChatOpen.value = false;
      setBodyChatVisible(false);
    } else {
      await chatwoot.show();
      globalIsChatOpen.value = true;
      setBodyChatVisible(true);
    }
  }

  return {
    toggleChat,
    isChatOpen: readonly(isChatOpen),
    isLoading: readonly(isLoading),
  };
}
